'use client';
import { type CSSProperties, useState } from 'react';
import { useRef } from 'react';
import { cn } from '../../_lib/utils';
import type { NavDropdownProps } from './NavDropdown';
import { cubicBezier, motion } from 'motion/react';
import { Text } from '../../Text';
import { Anchor } from '../../Anchor';
import { IconResolver } from '../../IconResolver';
import { Media } from '../../Media';
import { Card } from '../../Card';

type TabDataProps = NavDropdownProps & {
	active: boolean;
	setActiveTab(value: number | undefined): void;
	anyActiveTab: boolean;
	featuredText?: string;
	dropdownButtonCenterPosition: { center: number; navWidth: number };
	activeDesktopTabWidthLeftPosition: Position | null;
	setActiveDesktopTabWidthLeftPosition(value: Position | null): void;
};

export function DesktopTabData(props: TabDataProps) {
	const dataRef = useRef<HTMLDivElement>(null);
	const [allowOverflow, setAllowOverflow] = useState(false);
	const [show, setShow] = useState(false);
	const [animationTrigger, setAnimationTrigger] = useState(false);
	const [animationSetUp, setAnimationSetUp] = useState(false);
	const theme = 'light';

	const featuredText = props.featuredText ?? 'Featured';

	const cols =
		(props.featuredCards !== undefined ? 1 : 0) +
		(props.groups?.reduce((acc, el) => {
			if (el.cards !== undefined) {
				acc++;
			}
			if (el.items !== undefined) {
				acc++;
			}

			return acc;
		}, 0) ?? 0);

	if (props.active && !allowOverflow) {
		setTimeout(() => {
			setAllowOverflow(true);
		}, 300);
	}

	if (!props.active && allowOverflow) {
		setAllowOverflow(false);
	}

	if (
		props.active &&
		show &&
		!animationTrigger &&
		animationSetUp &&
		props.dropdownButtonCenterPosition.center > 0 &&
		props.dropdownButtonCenterPosition.navWidth > 0 &&
		props.activeDesktopTabWidthLeftPosition === null
	) {
		if (dataRef.current?.clientWidth && dataRef.current?.clientWidth > 0) {
			const width = dataRef.current?.clientWidth;
			const position = calculateElementPosition(
				props.dropdownButtonCenterPosition.navWidth,
				width,
				props.dropdownButtonCenterPosition.center,
			);

			props.setActiveDesktopTabWidthLeftPosition(position);

			setTimeout(() => {
				setAnimationTrigger(true);
			}, 50);
		} else {
			setAnimationSetUp(false);
			setShow(false);
			setAnimationTrigger(false);
			props.setActiveTab(undefined);
			setTimeout(() => {
				props.setActiveDesktopTabWidthLeftPosition(null);
			}, 300);
		}
	}

	if (props.active && !show) {
		props.setActiveDesktopTabWidthLeftPosition(null);
		setShow(true);
		if (!animationSetUp) {
			setTimeout(() => {
				setAnimationSetUp(true);
			}, 10);
		}
	}

	if (!props.active && show) {
		if (animationTrigger) {
			setAnimationSetUp(false);
			setAnimationTrigger(false);
		}

		if (!props.anyActiveTab) {
			setTimeout(() => {
				setShow(false);
			}, 300);
		} else {
			setShow(false);
		}
	}

	return (
		show && (
			<motion.div
				ref={dataRef}
				className='w-fit p-9 flex flex-col gap-9 max-h-[calc(100vh-var(--header-offset))] overflow-auto lg:grid lg:grid-cols-3 min-[75rem]:grid-cols-(--cols) bg-nav-secondary-background shadow-xl rounded-lg'
				initial={{ opacity: 0, y: 0 }}
				animate={{ opacity: props.active ? 1 : 0, y: props.active ? 10 : 0 }}
				transition={{
					duration: 0.3,
					delay: props.active ? 0.1 : 0,
					type: 'keyframes',
					ease: cubicBezier(0.33, 1, 0.68, 1),
				}}
				style={
					{
						pointerEvents: props.active ? 'auto' : 'none',
						'--cols': `repeat(${cols}, minmax(0, 1fr))`,
					} as CSSProperties
				}
				data-lenis-prevent
			>
				{props.groups &&
					props.groups.length > 0 &&
					props.groups.map((group, i) => {
						const span = (group.cards !== undefined ? 1 : 0) + (group.items !== undefined ? 1 : 0);

						return (
							<div
								key={`${group.label}-${i}-desktop-tab-data`}
								className='col-span-(--span) flex flex-col'
								style={{ '--span': span } as CSSProperties}
								data-brand={group.brand}
								data-theme={theme}
							>
								<Text as='h3' className='text-text-secondary pb-6 shrink-0 grow-0' styleType='label-md'>
									{group.label}
								</Text>

								<div className='grow-1 flex flex-col gap-spacing-sm lg:flex-row'>
									{group.cards && group.cards.length > 0 && (
										<div className='[flex-basis:0] grow-1 flex w-full'>
											{group.cards.map((card, j) => {
												const isExternal: boolean = !(
													card.href?.startsWith('/') || card.href?.startsWith(process.env.NEXT_PUBLIC_SITE_URL)
												);

												return (
													<Anchor
														className='flex w-full transition-transform duration-normal ease-smooth hover:scale-[102.5%]'
														key={`${card.title}-${j}-desktop-tab-data`}
														href={card.href}
														target={isExternal ? '_blank' : undefined}
													>
														<div
															className='w-full rounded-sm flex flex-col transition-colors duration-fast bg-nav-secondary-item-background-brand border border-nav-secondary-item-background-brand hover:bg-nav-secondary-item-background-brand-hover'
															onClick={() => {
																props.setActiveTab(undefined);
																setTimeout(() => {
																	props.setActiveDesktopTabWidthLeftPosition(null);
																}, 300);
															}}
															data-brand={card.brand}
															data-theme={theme}
														>
															<div className='p-spacing-md flex flex-col justify-center gap-0.5'>
																{card.title && (
																	<Text className='inline-block' styleType='heading-4xs'>
																		{card.title}

																		{isExternal && (
																			<IconResolver className='text-text-brand ml-2 inline-block w-4 h-4' icon='arrow-square-out' />
																		)}
																	</Text>
																)}

																{card.description && (
																	<Text className='text-text-secondary' styleType='body-sm'>
																		{card.description}
																	</Text>
																)}
															</div>

															{card.image && (
																<Media
																	className='mt-auto aspect-square overflow-hidden [&_img]:h-full h-fit md:h-auto'
																	image={card.image}
																/>
															)}
														</div>
													</Anchor>
												);
											})}
										</div>
									)}

									{group.items && group.items.length > 0 && (
										<div className='[flex-basis:0] grow-1 flex flex-col gap-1 -mt-responsive-3xs'>
											{group.items.map((item, j) => {
												const isExternal: boolean = !(
													item.href?.startsWith('/') || item.href?.startsWith(process.env.NEXT_PUBLIC_SITE_URL)
												);
												return (
													<Anchor key={`${item.title}-${j}-desktop-tab-data`} href={item.href} target={isExternal ? '_blank' : undefined}>
														<div
															className={cn(
																'rounded-sm p-spacing-3xs flex items-center gap-spacing-2xs transition-colors duration-fast hover:bg-nav-secondary-item-background-brand-subtle-hover',
																!item.image && 'px-responsive-2xs -mx-responsive-2xs',
															)}
															onClick={() => {
																props.setActiveTab(undefined);
																setTimeout(() => {
																	props.setActiveDesktopTabWidthLeftPosition(null);
																}, 300);
															}}
															data-brand={item.brand}
															data-theme={theme}
														>
															{item.image && (
																<Media
																	className='bg-background-secondary shrink-0 aspect-square rounded-sm border border-border-primary w-12 max-w-12 overflow-hidden [&_img]:h-full h-fit md:h-auto'
																	image={item.image}
																/>
															)}

															<div className='min-h-12 flex flex-col justify-center gap-0.5'>
																{item.title && (
																	<Text styleType='heading-5xs'>
																		{item.title}

																		{isExternal && (
																			<IconResolver className='text-text-brand ml-2 inline-block w-4 h-4' icon='arrow-square-out' />
																		)}
																	</Text>
																)}

																{item.description && (
																	<Text className='text-text-secondary' styleType='body-xs'>
																		{item.description}
																	</Text>
																)}
															</div>
														</div>
													</Anchor>
												);
											})}
										</div>
									)}
								</div>
							</div>
						);
					})}

				{props.featuredCards && props.featuredCards.length > 0 && (
					<div className='flex flex-col gap-spacing-3xs'>
						<Text as='h3' className='text-text-secondary p-spacing-3xs' styleType='label-md'>
							{featuredText}
						</Text>

						{props.featuredCards?.map((item, i) => (
							<div
								key={`${featuredText}-${i}-desktop-tab-data`}
								onClick={() => {
									props.setActiveTab(undefined);
									setTimeout(() => {
										props.setActiveDesktopTabWidthLeftPosition(null);
									}, 300);
								}}
							>
								<Card {...item} layout='horizontal' size='sm' />
							</div>
						))}
					</div>
				)}
			</motion.div>
		)
	);
}

type Position = {
	left?: number | undefined;
	right?: number | undefined;
};

/**
 * Calculates the left and right positioning for an element within a container.
 *
 * @param containerWidth - Width of the parent container
 * @param elementWidth - Width of the element to position
 * @param targetCenter - The desired center position for the element
 * @returns Position object with left and/or right values
 */
const calculateElementPosition = (containerWidth: number, elementWidth: number, targetCenter: number): Position => {
	// Calculate the position where the element would start if perfectly centered
	const idealLeftPosition = targetCenter - elementWidth / 2;

	// If the element would extend beyond the left edge of the container
	if (idealLeftPosition < 0) {
		return { left: 0, right: undefined };
	}

	// If the element would extend beyond the right edge of the container
	const idealRightEdge = idealLeftPosition + elementWidth;
	if (idealRightEdge > containerWidth) {
		return { right: 0, left: undefined };
	}

	// If the element fits perfectly within the container when centered
	return { left: idealLeftPosition, right: undefined };
};
